/**
 * [ELU-5500] Computed appointment statuses that are eligible for tenant-admin
 * note recovery: the encounter has happened (or is happening) and there is no
 * signed note yet.
 *
 *  - COMPLETED — encounter ended, but no note at all (failed, never generated,
 *    or stuck).
 *  - SUMMARY_AVAILABLE — an unsigned summary/draft note exists, but it may be
 *    incomplete or need regenerating. Recovery overwrites the unsigned draft.
 *  - ACTIVE — still in session; regenerating finalizes the appointment, so the
 *    UI requires an extra acknowledgement (see isActiveAppointmentStatus).
 *
 * Everything else is intentionally excluded: SIGNED / AMENDED / AMENDING (a
 * finalized note exists — regenerating it is an eluve-admin action, not a
 * clinic-admin one), NOT_STARTED (nothing happened), and CANCELLED
 * (intentionally abandoned). A usable transcript is additionally required by the
 * caller.
 */
const RECOVERABLE_STATUSES = new Set<string>([
  'COMPLETED',
  'SUMMARY_AVAILABLE',
  'ACTIVE',
]);

/**
 * True when an appointment's computed status makes it a candidate for note
 * recovery (encounter active or completed, with no note yet).
 */
export const isRecoverableStatus = (statusLabel: string): boolean =>
  RECOVERABLE_STATUSES.has(statusLabel);

/**
 * True when the appointment is still active (in session / not yet ended).
 * Regenerating a note for an active appointment finalizes it, so the UI requires
 * an extra confirmation before proceeding.
 */
export const isActiveAppointmentStatus = (statusLabel: string): boolean =>
  statusLabel === 'ACTIVE';

/**
 * True when a recoverable appointment already has a note — an unsigned summary
 * draft (SUMMARY_AVAILABLE). These are hidden by default and only listed when
 * the "Show appointments with notes" toggle is on.
 */
export const isAppointmentWithNoteStatus = (statusLabel: string): boolean =>
  statusLabel === 'SUMMARY_AVAILABLE';
